import {
  cancelOrgInvitation,
  changeOrgUserRole,
  inviteOrgUser,
  removeOrgUser,
  resetOrgUserMfa,
  useListOrgInvitations,
  useListOrgUsers,
  type OrgUserOut,
  type PendingInvitationOut,
  type Role,
} from '@myrentalmate/api-client';
import { Alert, Button, Chip, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import { DataTable, type DataColumn } from '../../components/DataTable';
import { FormDialog } from '../../components/FormDialog';
import { InviteLinkDialog } from '../../components/InviteLinkDialog';
import { EMAIL_FIELD, required, type Field } from '../../components/FormFields';
import { okData, okItems } from '../../lib/apiData';
import { apiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/format';
import { ROLE_LABELS, useMe } from '../../lib/me';
import { TablePager, useTablePage } from '../../components/TablePager';

const STAFF_ROLES: Role[] = [
  'org_admin',
  'property_manager',
  'leasing_agent',
  'accountant',
  'maintenance_tech',
  'read_only',
];
const ROLE_FIELD: Field = {
  name: 'role',
  label: 'Role',
  type: 'select',
  required: true,
  options: STAFF_ROLES.map((role) => ({ value: role, label: ROLE_LABELS[role] })),
};
const INVITE_FIELDS: Field[] = [{ ...EMAIL_FIELD, required: true }, ROLE_FIELD];

type Action =
  | { kind: 'invite' }
  | { kind: 'role' | 'remove' | 'reset'; user: OrgUserOut }
  | { kind: 'link'; url: string };

export function UsersSettingsPage() {
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const paging = useTablePage();
  const userQuery = useListOrgUsers(paging.params);
  const users = okItems(userQuery.data);
  const pending = okData(useListOrgInvitations().data)?.items ?? [];
  const [action, setAction] = useState<Action | null>(null);
  const [error, setError] = useState<string | null>(null);
  const close = () => setAction(null);

  async function cancel(invitation: PendingInvitationOut) {
    setError(null);
    try {
      await cancelOrgInvitation(invitation.id);
      await queryClient.invalidateQueries();
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  }

  const name = (user: OrgUserOut) => user.display_name || user.email;
  const columns: DataColumn<OrgUserOut>[] = [
    { label: 'Name', render: name },
    { label: 'Email', render: (u) => u.email },
    { label: 'Role', render: (u) => ROLE_LABELS[u.role] },
    {
      label: 'Two-factor',
      render: (u) => (
        <Chip
          size="small"
          label={u.mfa_enabled ? 'On' : 'Off'}
          color={u.mfa_enabled ? 'success' : 'default'}
        />
      ),
    },
    {
      label: 'Last sign-in',
      render: (u) => (u.last_sign_in_at ? formatDateTime(u.last_sign_in_at) : 'Never'),
    },
    {
      label: 'Actions',
      align: 'right',
      render: (u) => (
        <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
          <Button
            size="small"
            disabled={!STAFF_ROLES.includes(u.role)}
            onClick={() => setAction({ kind: 'role', user: u })}
          >
            Edit
          </Button>
          {u.mfa_enabled && u.user_id !== me?.id && (
            <Button size="small" onClick={() => setAction({ kind: 'reset', user: u })}>
              Reset two-factor
            </Button>
          )}
          <Button
            size="small"
            color="error"
            disabled={u.user_id === me?.id}
            onClick={() => setAction({ kind: 'remove', user: u })}
          >
            Remove
          </Button>
        </Stack>
      ),
    },
  ];
  const inviteColumns: DataColumn<PendingInvitationOut>[] = [
    { label: 'Email', render: (i) => i.sent_to ?? '–' },
    { label: 'Role', render: (i) => ROLE_LABELS[i.role] },
    { label: 'Expires', render: (i) => formatDateTime(i.expires_at) },
    {
      label: 'Actions',
      align: 'right',
      render: (i) => (
        <Button size="small" color="error" onClick={() => void cancel(i)}>
          Cancel
        </Button>
      ),
    },
  ];

  return (
    <Stack spacing={3}>
      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <Typography color="text.secondary" sx={{ flexGrow: 1 }}>
          Who can sign in to your business, and what they can do.
        </Typography>
        <Button variant="contained" onClick={() => setAction({ kind: 'invite' })}>
          Add user
        </Button>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      <DataTable columns={columns} rows={users} rowKey={(u) => u.user_id} />
      <TablePager paging={paging} total={okData(userQuery.data)?.total ?? 0} />
      {pending.length > 0 && (
        <Stack spacing={1}>
          <Typography variant="h6">Waiting to Accept</Typography>
          <DataTable columns={inviteColumns} rows={pending} rowKey={(i) => i.id} />
        </Stack>
      )}

      {action?.kind === 'invite' && (
        <InviteDialog onClose={close} onInvited={(url) => setAction({ kind: 'link', url })} />
      )}
      {action?.kind === 'link' && <InviteLinkDialog url={action.url} onClose={close} />}
      {action?.kind === 'role' && (
        <FormDialog
          title={`Edit ${name(action.user)}`}
          intro="Name, phone and email are theirs to change, under their own profile."
          fields={[ROLE_FIELD]}
          initial={{ role: action.user.role }}
          onSubmit={(values) =>
            changeOrgUserRole(action.user.user_id, { role: required(values, 'role') as Role })
          }
          onClose={close}
        />
      )}
      {action?.kind === 'remove' && (
        <FormDialog
          title={`Remove ${name(action.user)}?`}
          intro="They lose access to this business right away. Their account and anything they did here stay on record."
          fields={[]}
          initial={{}}
          submitLabel="Remove"
          onSubmit={() => removeOrgUser(action.user.user_id)}
          onClose={close}
        />
      )}
      {action?.kind === 'reset' && (
        <FormDialog
          title={`Reset Two-Factor for ${name(action.user)}?`}
          intro="Use this when someone has lost their phone and their backup codes. We'll email to tell them it happened."
          fields={[]}
          initial={{}}
          submitLabel="Reset two-factor"
          onSubmit={() => resetOrgUserMfa(action.user.user_id)}
          onClose={close}
        />
      )}
    </Stack>
  );
}

function InviteDialog({
  onClose,
  onInvited,
}: {
  onClose: () => void;
  onInvited: (url: string) => void;
}) {
  // FormDialog closes itself after saving, so the link is handed over at that moment.
  const created = useRef<string | null>(null);
  return (
    <FormDialog
      title="Add User"
      intro="We'll email them a link to create their account and join your business."
      fields={INVITE_FIELDS}
      initial={{ email: '', role: 'property_manager' }}
      submitLabel="Send invitation"
      onSubmit={async (values) => {
        const answer = okData(
          await inviteOrgUser({
            email: required(values, 'email'),
            role: required(values, 'role') as Role,
          }),
        );
        created.current = answer?.invite_url ?? null;
      }}
      onClose={() => (created.current ? onInvited(created.current) : onClose())}
    />
  );
}
